import { HeaderCategoriesButton } from "@faire/retailer-visitor-shared/components/Header/desktop/HeaderCategoriesDropdown/HeaderCategoriesButton";
import { useNavigationCardQuery } from "@faire/retailer-visitor-shared/components/Header/desktop/HeaderCategoriesDropdown/useNavigationCardQuery";
import { HeaderDesktopLogo } from "@faire/retailer-visitor-shared/components/Header/desktop/HeaderDesktopLogo";
import { LoginSection } from "@faire/retailer-visitor-shared/components/Header/desktop/LoginSection";
import { HeaderCart } from "@faire/retailer-visitor-shared/components/Header/HeaderCart";
import { HeaderSearch } from "@faire/retailer-visitor-shared/components/Header/HeaderSearch";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useCanAddToCart } from "@faire/retailer-visitor-shared/lib/useCanAddToCart";
import { useIsSemDiscoverPage } from "@faire/retailer-visitor-shared/lib/useIsSemDiscoverPage";
import { useSettingIsPerfAppSemDiscoverTreatment } from "@faire/retailer-visitor-shared/settings/useSettingPerfAppSemDiscover";
import { Row } from "@faire/web/slate/Layout";
import {
  getSpacing,
  GrowingSpacer,
  Spacer,
} from "@faire/web/slate/spacing";
import { ClientPlatformType } from "@faire/web-api--source/indigofair/data/ClientPlatformType";
import React, { useCallback, useState } from "react";
import styled, { css } from "styled-components";

import { MAX_HEADER_WIDTH } from "../consts";

import { BlogEntryLink } from "./BlogEntryLink";
import { LanguageSelectorSection } from "./LanguageSelectorSection";
import { MarketLinksSection } from "./MarketLinksSection";
import { SellOnFaireLink } from "./SellOnFaireLink";

export const HeaderDesktopContent = () => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const [shouldExpandSearchBar, setShouldExpandSearchBar] =
    useState<boolean>(false);

  const isSemSimplifiedLpEnabled = useIsSemDiscoverPage();
  const isPerfAppSemDiscoverPageTreatment =
    useSettingIsPerfAppSemDiscoverTreatment();

  // prefetch navigation card when navigation redesign is enabled
  useNavigationCardQuery(ClientPlatformType.CLIENT_DESKTOP);

  // This function reference must be stable for page scrolling
  const onSearchFocus = useCallback(
    (element: HTMLDivElement | null, isOnToggle?: boolean) => {
      if (isOnToggle) {
        setShouldExpandSearchBar(
          !!(element && element.getBoundingClientRect()?.width < 300)
        );
      }
    },
    []
  );

  // This function reference must be stable for page scrolling
  const onSearchBlur = useCallback(() => {
    setShouldExpandSearchBar(false);
  }, []);

  const canAddToCart = useCanAddToCart();

  return (
    <Wrapper
      justify="center"
      align="center"
      $enableRedesign={isLoggedInRetailer}
    >
      <HeaderDesktopLogo omitStyling={isLoggedInRetailer} />
      {isLoggedInRetailer ? (
        <>
          <Spacer size="2x" />
          <HeaderCategoriesButton />
          <Spacer size="2x" />
        </>
      ) : null}
      {isPerfAppSemDiscoverPageTreatment ? (
        <GrowingSpacer />
      ) : (
        <HeaderSearch
          onFocus={onSearchFocus}
          onBlur={onSearchBlur}
          inputId="top-search"
          inputDataTestId="searchBarInput"
        />
      )}
      <Spacer width="2x" />
      {!shouldExpandSearchBar && !isSemSimplifiedLpEnabled ? (
        <>
          {!isLoggedInRetailer ? <LanguageSelectorSection /> : null}
          {!isLoggedInRetailer ? <BlogEntryLink /> : null}
          {!canAddToCart ? <SellOnFaireLink /> : null}
          <MarketLinksSection />
        </>
      ) : null}
      <LoginSection />
      {canAddToCart ? (
        <>
          <HeaderCart dataTestId="headerCartButton" />
          {isLoggedInRetailer ? null : <Spacer width="3x" />}
        </>
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled(Row)<{ $enableRedesign?: boolean }>`
  width: 100%;

  ${({ $enableRedesign }) =>
    $enableRedesign
      ? css`
          max-width: ${MAX_HEADER_WIDTH}px;
          margin: auto;
          padding: ${getSpacing("2x")} ${getSpacing("6x")} 0;
        `
      : ""}
`;
